import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import sessionHandling from "services/utils/notificationUtils";

export async function fetchTableResponse(url, { signal } = {}) {
  const token = localStorage.getItem("token");
  const response = await fetch(url, { signal, cache: "no-store", headers: { Authorization: `token ${token}` } });
  if (signal?.aborted) throw new DOMException("Request cancelled", "AbortError");
  if (response.status === 401 || response.status === 409) {
    if (response.status === 409) sessionHandling(token);
    else if (token === localStorage.getItem("token")) { localStorage.clear(); window.location.href = "/auth/login"; }
    throw new Error("Your session has expired.");
  }
  if (!response.ok) {
    let body;
    try { body = await response.json(); }
    catch { throw new Error("Could not load records. Please try again."); }
    throw new Error(body.errors?.map(e => e.message).join(" ") || body.detail || body.error || "Could not load records.");
  }
  return response;
}

export async function fetchTable(url, options) {
  const response = await fetchTableResponse(url, options);
  try { return await response.json(); }
  catch { throw new Error("Could not load records. Please try again."); }
}

// URL is the committed query. Local search/drawer drafts never issue requests.
export function assertListingResponse(result, queryString, aliases = {}) {
  const fail = () => {
    const error = new Error("The table service could not apply these filters. Reload this page to refresh the available features; if this continues, contact your administrator.");
    error.code = "listing_contract_mismatch";
    throw error;
  };
  if (!Array.isArray(result?.results) || !result?.pagination || !Array.isArray(result?.query?.defaults_applied)) fail();
  const normalize = (key, value) => {
    if (key === "page" || key === "page_size") return String(Number(value));
    if (key === "search") return String(value).trim().replace(/\s+/g, " ");
    return [...new Set((Array.isArray(value) ? value : String(value).split(",")).map(item => String(item).trim()))].sort().join(",");
  };
  for (const [key, value] of new URLSearchParams(queryString)) {
    const canonical = aliases[key] || key;
    if (!Object.prototype.hasOwnProperty.call(result.query, canonical) || normalize(canonical, result.query[canonical]) !== normalize(canonical, value)) fail();
  }
}

export function useTableQuery({ endpoint, enabled = true, refreshInterval = 0, fetcher = fetchTable, debounceMs = 350, queryAliases }) {
  const history = useHistory();
  const location = useLocation();
  const key = location.search;
  const state = Object.fromEntries(new URLSearchParams(key));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");
  const [failures, setFailures] = useState(0);
  const [inFlight, setInFlight] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [search, setSearch] = useState(state.search || "");
  const [paused, setPaused] = useState(false);
  const [hidden, setHidden] = useState(document.visibilityState === "hidden");
  const [newEvents, setNewEvents] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const current = useRef({});
  const request = useRef(null);
  const sequence = useRef(0);
  const visibilityRefresh = useRef(false);
  const debouncing = search !== (state.search || "");
  const blocked = paused || hidden || drawerOpen || searchFocused || debouncing;
  current.current = { endpoint, key, fetcher, enabled, data, blocked, queryAliases };

  const setParams = useCallback((changes, { replace = false, resetPage = true } = {}) => {
    const next = new URLSearchParams(history.location.search);
    if (resetPage) next.delete("page");
    Object.entries(changes).forEach(([name, value]) => {
      if (value === null || value === undefined || value === "") next.delete(name);
      else next.set(name, String(value));
    });
    const searchString = next.toString();
    const target = { ...history.location, search: searchString ? `?${searchString}` : "" };
    if (target.search !== history.location.search) history[replace ? "replace" : "push"](target);
  }, [history]);

  const run = useCallback(async (silent = false) => {
    const c = current.current;
    if (!c.enabled || (silent && (request.current || c.blocked))) return;
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    const id = ++sequence.current;
    setInFlight(true);
    if (!silent) { setLoading(true); setError(""); }
    try {
      const response = await c.fetcher(`${c.endpoint}${c.key || ""}`, { signal: controller.signal });
      if (controller.signal.aborted || id !== sequence.current || c.key !== current.current.key || c.endpoint !== current.current.endpoint) return;
      const result = response;
      assertListingResponse(result, c.key, c.queryAliases);
      if (silent && Number(new URLSearchParams(c.key).get("page") || 1) > 1) {
        if (result.pagination.total_count !== c.data?.pagination?.total_count) setNewEvents(true);
      } else {
        setData(result);
        setNewEvents(false);
        const suppliedPage = new URLSearchParams(c.key).get("page");
        if (suppliedPage && Number(suppliedPage) !== result.pagination.page) {
          setParams({ page: result.pagination.page }, { replace: true, resetPage: false });
        }
      }
      setFailures(0); setError(""); setUpdatedAt(Date.now());
    } catch (e) {
      if (controller.signal.aborted || id !== sequence.current) return;
      if (e.code === "listing_contract_mismatch") { setData(null); setFailures(0); }
      else if (silent) setFailures(n => n + 1);
      setError(e.message || "Could not load records.");
    } finally {
      if (id === sequence.current && !controller.signal.aborted) {
        request.current = null;
        setInFlight(false); setLoading(false); setReady(true);
      }
    }
  }, [setParams]);

  useEffect(() => {
    setSearch(new URLSearchParams(key).get("search") || "");
    setFailures(0); setNewEvents(false);
    run();
    return () => { sequence.current += 1; request.current?.abort(); request.current = null; };
  }, [endpoint, key, enabled, run]);
  useEffect(() => {
    if (!debouncing) return undefined;
    const timer = setTimeout(() => {
      const normalized = search.trim().replace(/\s+/g, " ");
      setSearch(normalized);
      setParams({ search: normalized });
    }, debounceMs);
    return () => clearTimeout(timer);
  }, [search, debouncing, debounceMs, setParams]);
  useEffect(() => {
    const visible = () => {
      const isHidden = document.visibilityState === "hidden";
      setHidden(isHidden);
      if (!isHidden) visibilityRefresh.current = true;
    };
    document.addEventListener("visibilitychange", visible);
    return () => document.removeEventListener("visibilitychange", visible);
  }, []);
  useEffect(() => {
    if (enabled && !blocked && !inFlight && visibilityRefresh.current) {
      visibilityRefresh.current = false;
      run(true);
    }
  }, [enabled, blocked, inFlight, run]);
  useEffect(() => {
    if (!enabled || !refreshInterval || blocked || inFlight) return undefined;
    const delay = failures ? Math.min(60000, 30000 * (2 ** (failures - 1))) : refreshInterval;
    const timer = setTimeout(() => run(true), delay);
    return () => clearTimeout(timer);
  }, [enabled, refreshInterval, blocked, inFlight, failures, updatedAt, run]);
  return { state, queryString: key, data, loading, ready, inFlight, error: failures > 0 && failures < 3 ? "" : error,
    setParams, search, setSearch, setSearchFocused, drawerOpen, setDrawerOpen,
    paused, setPaused, blocked, newEvents, refresh: () => run(false),
    updatedAt };
}
